"use client";
import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import { createClient } from "@/lib/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useRouter } from "next/navigation";

type ReportGenerationProgressContextType = {
  progress: number | null;
  setProgress: (progress: number) => void;
  reportUuid: string;
  setReportUuid: (reportUuid: string) => void;
  isGenerating: boolean;
  setIsGenerating: (isGenerating: boolean) => void;
};

// Create the context
const ReportGenerationProgressContext = createContext<
  ReportGenerationProgressContextType | undefined
>(undefined);

export function ReportGenerationProgressProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [progress, setProgress] = useState<number | null>(null);
  const [oldProgress, setOldProgress] = useState<number | null>(null);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [reportUuid, setReportUuid] = useState<string>("");
  const router = useRouter();
  const toastId = useRef<string | null>(null);
  const { toast, dismiss } = useToast();
  const supabase = createClient();

  useEffect(() => {
    if (!reportUuid) return;

    const fetchInitialProgress = async () => {
      const { data, error } = await supabase
        .from("report")
        .select("generation_progress")
        .eq("uuid", reportUuid)
        .single();

      if (error) {
        console.error("Error fetching report", error);
        return;
      }

      const generation_progress = data?.generation_progress ?? 100;
      setProgress(generation_progress);
      setOldProgress(generation_progress);
      setIsGenerating(generation_progress > 0 && generation_progress < 100);
    };

    fetchInitialProgress();

    const reportSubscription = supabase
      .channel("realtime report generation progress")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "report",
          filter: `uuid=eq.${reportUuid}`,
        },
        (payload: any) => {
          const newProgress = payload.new.generation_progress;

          // Only proceed if generation_progress has changed
          if (newProgress === oldProgress) return;

          // Update the progress and oldProgress states
          setOldProgress(newProgress);
          setProgress(newProgress);

          if (newProgress === 100) {
            setIsGenerating(false);
            toastId.current = toast({
              title: "Report generated",
              description: "The report has been generated successfully.",
              variant: "default",
              duration: Infinity,
              action: (
                <ToastAction
                  altText="View Report"
                  onClick={() => {
                    router.push(`/report/${reportUuid}`);
                    if (toastId.current) {
                      dismiss(toastId.current);
                    }
                  }}
                >
                  View
                </ToastAction>
              ),
            }).id;
          } else if (newProgress === -1) {
            setIsGenerating(false);
            toast({
              title: "Report generation failed",
              description: "There was an error while generating the report.",
              variant: "destructive",
            });
          } else {
            setIsGenerating(true);
          }
        }
      )
      .subscribe();

    return () => {
      reportSubscription.unsubscribe();
    };
  }, [reportUuid, supabase, oldProgress, toast, dismiss, router]);

  return (
    <ReportGenerationProgressContext.Provider
      value={{
        progress,
        setProgress,
        isGenerating,
        setIsGenerating,
        reportUuid,
        setReportUuid,
      }}
    >
      {children}
    </ReportGenerationProgressContext.Provider>
  );
}

// Custom hook to use the context
export function useReportGenerationProgress() {
  const context = useContext(ReportGenerationProgressContext);
  if (context === undefined) {
    throw new Error(
      "useReportGenerationProgress must be used within a ReportGenerationProgressProvider"
    );
  }
  return context;
}
